import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Badge } from '@vaiu/ui/badge';
import { Store, MapPin, Phone, Globe, DollarSign, CheckCircle, AlertCircle, User } from 'lucide-react';
import { i18n } from '@/utils/i18n';
import { extractValidAddress, formatAddressForDisplay, isValidString, type Address } from '@/utils/profileHelpers';

// Helper function to replace template variables in translations
const t = (key: string, replacements?: Record<string, string>) => {
  let translation = i18n.__(key as any);
  if (replacements) {
    Object.entries(replacements).forEach(([placeholder, value]) => {
      translation = translation.replace(new RegExp(`{{${placeholder}}}`, 'g'), value);
    });
  }
  return translation;
};

/**
 * Photo interface for type safety
 */
interface Photo {
  url?: string;
  photo_reference?: string;
}

/**
 * Public Profile Card Props
 */
interface PublicProfileCardProps {
  profile: {
    id: string;
    name: string;
    displayName?: string;
    businessName?: string;
    businessType?: string;
    email?: string;
    phone?: string;
    website?: string;
    address?: Address | string;
    logo?: string;
    photos?: string[] | Photo[];
    avatar?: string;
    isMerchant?: boolean;
    verification?: {
      isVerified?: boolean;
      kycStatus?: 'pending' | 'approved' | 'rejected';
      kybStatus?: 'pending' | 'approved' | 'rejected';
    };
    isPublished?: boolean;
  };
  paymentCurrency?: string;
}

/**
 * Reusable component for displaying public profile information
 * Used in PayPage and other places where a compact profile card is needed
 * 
 * @example
 * ```tsx
 * <PublicProfileCard 
 *   profile={{
 *     id: "123",
 *     name: "John Doe",
 *     isMerchant: false,
 *     email: "john@example.com"
 *   }}
 *   paymentCurrency="USD"
 * />
 * ```
 */
const PublicProfileCard: React.FC<PublicProfileCardProps> = ({ profile, paymentCurrency }) => {
  const [imageError, setImageError] = useState(false);

  const displayName = profile.displayName || profile.businessName || profile.name;
  const hasImage = profile.logo || profile.photos?.length > 0 || profile.avatar;
  
  // Get image source
  const getImageSource = () => {
    if (profile.avatar) return profile.avatar;
    if (profile.logo) return profile.logo;
    if (profile.photos && profile.photos.length > 0) {
      const firstPhoto = profile.photos[0];
      return typeof firstPhoto === 'string' 
        ? firstPhoto 
        : firstPhoto.url || firstPhoto.photo_reference;
    }
    return null;
  };

  // Render address component using shared utility
  const renderAddress = () => {
    if (!profile.isMerchant || !profile.address) return null;
    
    const address = extractValidAddress(profile.address);
    if (!address) return null;
    
    const formattedAddress = formatAddressForDisplay(address);
    if (!formattedAddress) return null;
    
    return (
      <div className="flex items-start gap-2" role="group" aria-label="Address">
        <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div>
          {formattedAddress.split('\n').map((line, index) => (
            <div key={index}>{line}</div>
          ))}
        </div>
      </div>
    );
  };

  const addressElement = renderAddress();
  const hasContactInfo = (profile.isMerchant && (addressElement || profile.phone || profile.website)) || 
                         (!profile.isMerchant && profile.phone);

  return (
    <Card>
      <CardHeader className="py-3 sm:py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {hasImage && !imageError ? (
              <img 
                src={getImageSource() || ''} 
                alt={`${displayName} ${profile.isMerchant ? 'business' : 'profile'} picture`}
                className="w-12 h-12 rounded-lg object-cover"
                onError={() => setImageError(true)}
                role="img"
                aria-label={`${displayName} ${profile.isMerchant ? 'business' : 'profile'} picture`}
              />
            ) : (
              <div 
                className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white"
                role="img"
                aria-label={`${displayName} ${profile.isMerchant ? 'business' : 'profile'} icon`}
              >
                {profile.isMerchant ? <Store className="w-6 h-6" /> : <User className="w-6 h-6" />}
              </div>
            )}
            <div>
              <CardTitle className="text-lg">{displayName}</CardTitle>
              {profile.isMerchant && profile.businessType && typeof profile.businessType === 'string' && (
                <CardDescription>
                  {profile.businessType}
                </CardDescription>
              )}
              {/* Show email for both merchants and users if available */}
              {profile.email && typeof profile.email === 'string' && (
                <CardDescription>
                  {profile.email}
                </CardDescription>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {profile.verification?.isVerified && (
              <Badge variant="default" className="gap-1">
                <CheckCircle className="w-3 h-3" />
                {t('vaiu.payment.verified')}
              </Badge>
            )}
            {profile.isMerchant && profile.verification?.kycStatus && (
              <Badge 
                variant={
                  profile.verification.kycStatus === 'approved' ? 'default' :
                  profile.verification.kycStatus === 'rejected' ? 'destructive' :
                  'secondary'
                }
                className="gap-1"
              >
                {profile.verification.kycStatus === 'approved' && <CheckCircle className="w-3 h-3" />}
                {profile.verification.kycStatus === 'rejected' && <AlertCircle className="w-3 h-3" />}
                {profile.verification.kycStatus === 'approved' ? t('common.kybVerified') :
                 profile.verification.kycStatus === 'rejected' ? t('common.kybRejected') :
                 t('common.kybPending')}
              </Badge>
            )}
            {profile.isMerchant && profile.isPublished === false && (
              <Badge variant="outline" className="text-xs">
                {t('common.notListed')}
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      {hasContactInfo && (
        <CardContent className="pt-0 pb-2 sm:pb-3">
          <div className="space-y-1 text-sm text-muted-foreground">
            {addressElement}
            {profile.phone && typeof profile.phone === 'string' && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <a 
                  href={`tel:${profile.phone}`} 
                  className="hover:underline"
                >
                  {profile.phone}
                </a>
              </div>
            )}
            {profile.isMerchant && profile.website && typeof profile.website === 'string' && (
              <div className="flex items-center gap-2">
                <Globe className="w-4 h-4" />
                <a 
                  href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  {profile.website.replace(/^https?:\/\//, '')}
                </a>
              </div>
            )}
            {paymentCurrency && (
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium">{t('common.currency')}: <span className="text-blue-600 dark:text-blue-400">{paymentCurrency}</span></span>
              </div>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default PublicProfileCard;

